/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
*/

import { Pagination, Subscription, SubscriptionList } from '../../data/interfaces';
import {
    CreateSubscriptionsAction, CREATE_SUBSCRIPTIONS_FAILURE, CREATE_SUBSCRIPTIONS_REQUEST,
    CREATE_SUBSCRIPTIONS_SUCCESS, DELETE_SUBSCRIPTIONS_FAILURE, DELETE_SUBSCRIPTIONS_REQUEST,
    DELETE_SUBSCRIPTIONS_SUCCESS, FETCH_SUBSCRIPTIONS_FAILURE, FETCH_SUBSCRIPTIONS_REQUEST,
    FETCH_SUBSCRIPTIONS_SUCCESS, SubscriptionsAction, UPDATE_SUBSCRIPTION_FAILURE,
    UPDATE_SUBSCRIPTION_REQUEST, UPDATE_SUBSCRIPTION_SUCCESS
} from '../types/subscriptions';

interface SubscriptionState {
    isLoading: boolean,
    isDeletingSubscription: boolean;
    subscriptions: SubscriptionList,
    error: string,
    fetchSuccess: boolean,
    updateSuccess: boolean,
}

const initialState: SubscriptionState = {
    isLoading: false,
    isDeletingSubscription: false,
    subscriptions: { count: 0, list: [], pagination: {} as Pagination },
    error: '',
    fetchSuccess: false,
    updateSuccess: false,
}

const subscriptionsReducer = (state = initialState, action: SubscriptionsAction | CreateSubscriptionsAction) => {
    switch (action.type) {
        case FETCH_SUBSCRIPTIONS_REQUEST:
            return {
                ...state,
                isLoading: true
            }
        case FETCH_SUBSCRIPTIONS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                subscriptions: action.payload
            }
        case FETCH_SUBSCRIPTIONS_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            }
        case UPDATE_SUBSCRIPTION_REQUEST:
            return {
                ...state,
                isLoading: true,
                updateSuccess: false
            }
        case UPDATE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                isLoading: false,
                updateSuccess: true,
                subscriptions: {
                    ...state.subscriptions,
                    list: [
                        ...state.subscriptions.list.filter((subscription) => {
                            return subscription.subscriptionId !== (action.payload as Subscription).subscriptionId;
                        }),
                        action.payload
                    ]
                }
            };
        case UPDATE_SUBSCRIPTION_FAILURE:
            return {
                ...state,
                isLoading: false,
                updateSuccess: false,
                error: action.payload
            }
        case CREATE_SUBSCRIPTIONS_REQUEST:
            return {
                ...state,
                isLoading: true
            }
        case CREATE_SUBSCRIPTIONS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                subscriptions: {
                    ...state.subscriptions,
                    list: [...state.subscriptions.list, action.payload],
                    count: state.subscriptions.count + 1
                }
            };
        case CREATE_SUBSCRIPTIONS_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            }
        case DELETE_SUBSCRIPTIONS_REQUEST:
            return {
                ...state,
                isDeletingSubscription: true
            }
        case DELETE_SUBSCRIPTIONS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isDeletingSubscription: false,
                subscriptions: {
                    ...state.subscriptions,
                    list: state.subscriptions.list.filter((subscription) => {
                        return subscription.subscriptionId !== action.payload;
                    }),
                    count: state.subscriptions.count - 1
                }
            };
        case DELETE_SUBSCRIPTIONS_FAILURE:
            return {
                ...state,
                isDeletingSubscription: false,
                isLoading: false,
                error: action.payload
            }
        default:
            return state;
    }
}

export default subscriptionsReducer;
